import React, { useCallback } from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';

import { SponsoredBrandCarouselV2 } from 'src/components/carousels/ads-carousels/sponsored-brand-carousel-v2';
import { CategoryPageRecommendations } from 'src/components/carousels/personalization/category-page-recommendations';
import { PRODUCT_GRID_TEST_ID } from 'src/components/product-list/product-list.constants';
import { ProductCardGrid } from 'src/components/product-card-grid';
import Card from 'src/components/product-card-v2/card';

import useUI from 'src/hooks/use-ui';
import { useAddToCartModal, findSingleWeightItem } from 'components/modals/add-to-cart';
import { useSponsoredProductHandlers } from 'src/utils/ads/hooks/sponsored-products/use-sponsored-product-handlers';
import { useCommerceComponents } from 'src/theme/provider/commerce-components/context';
import { tracker } from 'src/utils/analytics';

import { useGroupProductsByRows } from './use-group-products-by-rows';
import { getShowInterstitialCarousels } from './product-list.utils';
import { useProductListInjections } from './use-product-list-injections';
import { ProductListConciergeCard } from './product-list-concierge-card';

/**
 * @param {Object} props
 * @param {*} props.products
 * @param {*} props.dispensary
 * @param {*} props.trackerSource
 * @param {*} props.adPlacement
 * @param {*} props.productsCarouselQueries
 * @param {import('./product-list-concierge-content').ProductListConciergeEntry | null} [props.conciergeEntry]
 */
const ProductCardList = ({
  products,
  dispensary,
  trackerSource,
  adPlacement,
  productsCarouselQueries,
  conciergeEntry = null,
}) => {
  const { showSponsoredBrandsCarousel, showCategoryRecommendationsCarousel } = getShowInterstitialCarousels(
    productsCarouselQueries
  );
  const { sponsoredBrandQuery, personalizedProductsQuery } = productsCarouselQueries ?? {
    sponsoredBrandQuery: null,
    personalizedProductsQuery: null,
  };

  const { iterables } = useCommerceComponents();
  const pageInterstitials = iterables.CategoryPageInterstitials();
  const pageSlots = iterables.CategoryPageSlots();

  const { InterstitialComponent, SlotComponent } = useProductListInjections({
    interstitials: pageInterstitials,
    slots: pageSlots,
  });

  const UI = useUI();
  const { open: openAddToCartModal } = useAddToCartModal();

  // When interstitial is shown, use [2, 1, 1] to position it after 3 rows (same spot with or without ads)
  const hasInterstitial = !!InterstitialComponent;
  const rows = hasInterstitial ? [2, 1, 1] : [2, 2];

  const { getItemsGroup } = useGroupProductsByRows({
    products,
    isListView: false,
    rows,
    slotComponent: SlotComponent,
  });

  const { handleSponsoredProductClick, handleSponsoredProductImpression } = useSponsoredProductHandlers({
    trackerSource,
    adPlacement,
  });

  const handleClick = useCallback(
    (product, index) => {
      tracker.setContext({ activeProductPosition: index });

      tracker.productClicked({
        product,
        position: index,
        trackerSource,
        placement: 'menu-page-product-card-list',
      });

      UI.activeProductSource = trackerSource;
      UI.setProductPosition({
        productId: product.id,
        productIndex: index,
      });

      handleSponsoredProductClick(product, index);
    },
    [UI, trackerSource, handleSponsoredProductClick]
  );

  const handleQuickAddClick = useCallback(
    (product, index) => {
      openAddToCartModal({
        ...findSingleWeightItem(product),
        product,
        dispensary,
        trackerSource,
      });
      handleClick(product, index);
    },
    [dispensary, handleClick, openAddToCartModal, trackerSource]
  );

  const renderItem = (item) => {
    if (item.type === 'slot') {
      const SlotComp = item.component;
      return <SlotComp key='slot-component' />;
    }

    const { product, index } = item;
    return (
      <Card
        data-testid='product-grid-item'
        key={product.id}
        product={product}
        productIndex={index}
        onClick={handleClick}
        onView={handleSponsoredProductImpression}
        onQuickAddClick={handleQuickAddClick}
      />
    );
  };

  return (
    <ProductCardGrid dataTestId={PRODUCT_GRID_TEST_ID}>
      {getItemsGroup(0).map(renderItem)}

      {showSponsoredBrandsCarousel && (
        <AdUnit data-testid='ad-unit-sponsored-brands-carousel'>
          <SponsoredBrandCarouselV2 sponsoredBrandQuery={sponsoredBrandQuery} adPlacement='category.carousel' />
        </AdUnit>
      )}

      {!hasInterstitial && conciergeEntry && <ProductListConciergeCard {...conciergeEntry} />}

      {getItemsGroup(1).map(renderItem)}

      {InterstitialComponent && (
        <InterstitialComponentWrapper data-testid='product-grid-interstitial-component'>
          <InterstitialComponent />
        </InterstitialComponentWrapper>
      )}

      {hasInterstitial && conciergeEntry && <ProductListConciergeCard {...conciergeEntry} />}

      {hasInterstitial && getItemsGroup(2).map(renderItem)}

      {showCategoryRecommendationsCarousel && (
        <AdUnit data-testid='ad-unit-category-recommendations-carousel'>
          <CategoryPageRecommendations personalizedProductsQuery={personalizedProductsQuery} />
        </AdUnit>
      )}

      {getItemsGroup(hasInterstitial ? 3 : 2).map(renderItem)}
    </ProductCardGrid>
  );
};

export default observer(ProductCardList);

const InterstitialComponentWrapper = styled.div`
  grid-column: 1 / -1;

  @media (min-width: 960px) and (max-width: 1240px) {
    width: var(--kiosk-pro-content-width, calc(100vw - 270px));
  }
`;

const AdUnit = styled.aside`
  grid-column: 1 / -1;

  @media (min-width: 960px) and (max-width: 1240px) {
    width: var(--kiosk-pro-content-width, calc(100vw - 270px));
  }

  & > div {
    padding: 0;
  }
`;
